import React, { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CMP = { quote: "clicked quote", call: "clicked call" };

const countBy = (items, fn) => {
  const m = {};
  items.forEach((x) => { const k = fn(x); if (k) m[k] = (m[k] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
};

export const adLabel = (ad) => (ad && (ad.utm_source || ad.utm_campaign)
  ? [ad.utm_source, ad.utm_medium, ad.utm_campaign].filter(Boolean).join(" / ")
  : "");

const PERIODS = [["month", "This month"], ["last", "Last month"], ["all", "All time"]];
const inPeriod = (iso, p) => {
  if (p === "all") return true;
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  const ref = p === "month" ? now : new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
};

const Column = ({ title, rows, testid, empty = "No tracked leads in this period." }) => (
  <div data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</p>
    {rows.length === 0 ? <p className="text-sm text-slate-400">{empty}</p> : (
      <ul className="space-y-1">
        {rows.map(([page, n], i) => (
          <li key={page} className="flex justify-between gap-3 text-sm" data-testid={`${testid}-row-${i}`}>
            <span className="text-[#0a1f44] truncate">{page}</span><span className="font-bold text-[#0a1f44]">{n}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const CompareStats = ({ list, clicks, period }) => {
  const c = clicks.filter((x) => inPeriod(x.created_at, period));
  const n = (a) => c.filter((x) => x.action === a).length;
  return (
    <div className="mt-6 pt-4 border-t border-slate-100" data-testid="compare-stats">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Comparison page (/hellotech-alternative)</p>
      <div className="flex flex-wrap gap-6 text-sm text-[#0a1f44]">
        <span>Leads tagged: <strong data-testid="compare-stats-leads">{list.filter((x) => x.compare_click).length}</strong></span>
        <span>Quote button clicks: <strong data-testid="compare-stats-quote">{n("quote")}</strong></span>
        <span>Call button clicks: <strong data-testid="compare-stats-call">{n("call")}</strong></span>
      </div>
    </div>
  );
};

export const LeadSourceStats = ({ items, token }) => {
  const [period, setPeriod] = useState("month");
  const [clicks, setClicks] = useState([]);
  useEffect(() => {
    fetch(`${API}/admin/compare-clicks`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()).then((d) => setClicks(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  const list = items.filter((x) => inPeriod(x.created_at, period));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4" data-testid="lead-source-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-bold text-[#0a1f44]">Leads by page and ad</p>
        <div className="inline-flex p-1 rounded-full bg-slate-100" data-testid="lead-period-toggle">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`lead-period-${k}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${period === k ? "bg-white text-[#0a1f44] shadow" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-4" data-testid="lead-period-total">{list.length} lead{list.length === 1 ? "" : "s"} in this period, including instant quotes. Page tracking and ad tracking only cover leads sent after each was added.</p>
      <div className="grid md:grid-cols-3 gap-6">
        <Column title="Sent from page" rows={countBy(list, (x) => x.submit_page)} testid="lead-source-submit" />
        <Column title="First landing page" rows={countBy(list, (x) => x.landing_page)} testid="lead-source-landing" />
        <Column title="Ad campaign" rows={countBy(list, (x) => adLabel(x.ad))} testid="lead-source-ad" empty="No ad leads in this period." />
      </div>
      <CompareStats list={list} clicks={clicks} period={period} />
    </div>
  );
};

export const LeadSourceLine = ({ q }) => (
  <>
    {(q.submit_page || q.landing_page) && (
      <div className="text-xs text-slate-400 mb-0.5" data-testid={`lead-source-${q.id}`}>
        Landed: {q.landing_page || "-"} · Sent from: {q.submit_page || "-"}
      </div>
    )}
    {q.fireplace && (
      <div className="text-xs font-semibold text-[#3b9dff] mb-0.5" data-testid={`lead-fireplace-${q.id}`}>Above a fireplace</div>
    )}
    {q.compare_click && (
      <div className="text-xs font-semibold text-emerald-700 mb-0.5" data-testid={`lead-compare-${q.id}`}>
        From comparison page ({CMP[q.compare_click] || q.compare_click})
      </div>
    )}
    {adLabel(q.ad) && (
      <div className="text-xs font-semibold text-orange-600 mb-0.5" data-testid={`lead-ad-${q.id}`}>
        Ad: {adLabel(q.ad)}{q.ad.utm_term ? ` · ${q.ad.utm_term}` : ""}{q.ad.utm_content ? ` · ${q.ad.utm_content}` : ""}
      </div>
    )}
  </>
);
